// src/components/GoogleLoginButton.jsx
import React, { useState, useEffect, useRef } from 'react'
import { Settings } from 'lucide-react'
import { supabase } from '../supabaseClient'
import { NicknameSetupModal } from './NicknameSetupModal'
import { MyPageModal } from './MyPageModal'

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" style={{ flexShrink: 0 }}>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
)

export function GoogleLoginButton() {
  const [loading, setLoading] = useState(false)
  const [user, setUser] = useState(null)
  const [nickname, setNickname] = useState(null)
  const [showSetup, setShowSetup] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [showMyPage, setShowMyPage] = useState(false)
  const [showBanned, setShowBanned] = useState(false)
  const menuRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const fetchNickname = async (userId) => {
    const { data: isBanned } = await supabase.rpc('check_if_blacklisted')
    if (isBanned) {
      await supabase.auth.signOut({ scope: 'local' })
      setUser(null)
      setNickname(null)
      setShowSetup(false)
      setShowBanned(true)
      return
    }

    const { data, error } = await supabase
      .from('profiles')
      .select('nickname')
      .eq('user_id', userId)
      .limit(1)

    if (error) {
      setNickname(null)
      setShowSetup(true)
      return
    }

    const found = data?.[0]?.nickname ?? null
    if (found) {
      setNickname(found)
      setShowSetup(false)
    } else {
      setNickname(null)
      setShowSetup(true)
    }
  }

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT') {
        setUser(null)
        setNickname(null)
        setShowSetup(false)
      } else if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
        const u = session?.user ?? null
        setUser(u)
        if (u) fetchNickname(u.id)
      }
    })
    return () => subscription.unsubscribe()
  }, [])

  const handleGoogleLogin = async () => {
    setLoading(true)
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin + window.location.pathname,
        queryParams: { prompt: 'select_account' },
      },
    })
    if (error) {
      console.error('ログインエラー:', error.message)
      setLoading(false)
    }
  }

  const handleLogout = async () => {
    setLoading(true)
    await supabase.auth.signOut()
    setUser(null)
    setNickname(null)
    setShowSetup(false)
    setLoading(false)
  }

  const handleSetupCancel = async () => {
    await supabase.auth.signOut({ scope: 'local' })
    setUser(null)
    setNickname(null)
    setShowSetup(false)
  }

  const handleSetupComplete = (newNickname) => {
    setNickname(newNickname)
    setShowSetup(false)
  }

  return (
    <>
      {showBanned && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 20, padding: '32px 28px', maxWidth: 360, width: '100%', textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🚫</div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1f2937', marginBottom: 12 }}>アクセスが制限されています</h2>
            <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.7, marginBottom: 24 }}>
              このアカウントはHowToonのご利用が制限されています。<br />
              詳しくはお問い合わせよりご連絡ください。
            </p>
            <button
              onClick={() => setShowBanned(false)}
              style={{ background: '#f3f4f6', border: 'none', borderRadius: 12, padding: '10px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#374151' }}
            >
              閉じる
            </button>
          </div>
        </div>
      )}

      {user && showSetup && (
        <NicknameSetupModal
          userId={user.id}
          onComplete={handleSetupComplete}
          onCancel={handleSetupCancel}
        />
      )}

      {user && showMyPage && (
        <MyPageModal
          userId={user.id}
          currentNickname={nickname}
          onClose={() => setShowMyPage(false)}
          onSaved={(newNickname) => {
            setNickname(newNickname)
            setShowMyPage(false)
          }}
        />
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setShowMenu(v => !v)}
            style={{
              position: 'relative',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 32, height: 32,
              background: showMenu ? '#ede9fe' : '#f3f4f6',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              color: showMenu ? '#7c3aed' : '#6b7280',
              transition: 'background 0.15s, color 0.15s',
            }}
          >
            <Settings size={16} />
          </button>

          {showMenu && (
            <div style={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              right: 0,
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              minWidth: 160,
              overflow: 'hidden',
              zIndex: 300,
            }}>
              {user ? (
                <>
                  <button
                    onClick={() => { setShowMenu(false); setShowMyPage(true) }}
                    style={{ display: 'block', width: '100%', padding: '11px 16px', background: 'none', border: 'none', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#374151', cursor: 'pointer' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f5f3ff' }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'none' }}
                  >
                    ニックネーム編集
                  </button>
                  <div style={{ height: 1, background: '#f3f4f6', margin: '0 10px' }} />
                  <button
                    onClick={handleLogout}
                    disabled={loading}
                    style={{ display: 'block', width: '100%', padding: '11px 16px', background: 'none', border: 'none', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#ef4444', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2' }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'none' }}
                  >
                    ログアウト
                  </button>
                </>
              ) : (
                <button
                  onClick={() => { setShowMenu(false); handleGoogleLogin() }}
                  disabled={loading}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '11px 16px', background: 'none', border: 'none', textAlign: 'left', fontSize: 13, fontWeight: 600, color: '#374151', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f5f3ff' }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'none' }}
                >
                  <GoogleIcon />
                  {loading ? 'ログイン中...' : 'Googleでログイン'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
